import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  if (imageUrl.startsWith('/')) {
    if (imageUrl.startsWith('/uploads/') && !imageUrl.startsWith('/api/')) {
      return `${API_URL}/api${imageUrl}`;
    }
    return `${API_URL}${imageUrl}`;
  }
  return imageUrl;
};

// Extract key features from description
const extractFeatures = (description, maxFeatures = 3) => {
  if (!description) return [];
  
  // Clean up description - remove section headers
  const cleanDesc = description
    .replace(/^(Description|Spec|File Setup|Setup)[\s\n]*/gi, '')
    .replace(/Description\n/gi, '')
    .trim();
  
  // Try to extract bullet-like features or sentences
  const features = [];
  
  // Look for common feature patterns
  const patterns = [
    /(?:^|\n)[-•]\s*([^-•\n]{10,80})/gm,  // Bullet points
    /(?:^|\n)([A-Z][^.!?\n]{15,80}[.!?])/gm,  // Sentences starting with capital
  ];
  
  for (const pattern of patterns) {
    const matches = cleanDesc.matchAll(pattern);
    for (const match of matches) {
      const feature = match[1]?.trim();
      if (feature && feature.length > 10 && feature.length < 80 && features.length < maxFeatures) {
        // Clean up and avoid duplicates
        const cleanFeature = feature.replace(/^[-•]\s*/, '').trim();
        if (!features.some(f => f.toLowerCase() === cleanFeature.toLowerCase())) {
          features.push(cleanFeature);
        }
      }
    }
    if (features.length >= maxFeatures) break;
  }
  
  // If no features found, create from description chunks
  if (features.length === 0 && cleanDesc.length > 20) {
    const sentences = cleanDesc.split(/[.!?]+/).filter(s => s.trim().length > 15);
    for (let i = 0; i < Math.min(sentences.length, maxFeatures); i++) {
      const sentence = sentences[i].trim();
      if (sentence.length > 10 && sentence.length < 100) {
        features.push(sentence.substring(0, 70) + (sentence.length > 70 ? '...' : ''));
      }
    }
  }
  
  return features.slice(0, maxFeatures);
};

// Map source_category codes to display names
const CATEGORY_NAMES = {
  'VNL': 'Vinyl Banners',
  'BAN': 'Banners',
  'MSH': 'Mesh Banners',
  'FAB': 'Fabric',
  'ADH': 'Adhesive',
  'RGD': 'Rigid Signs',
  'FLG': 'Flags',
  'AFR': 'A-Frames',
  'BNS': 'Banner Stands',
  'TBL': 'Table Throws',
  'TNT': 'Tents',
  'SEG': 'SEG Displays',
  'TDP': 'Trade Show',
  'WAL': 'Wall Art',
  'CHL': 'Chalk',
  'FTH': 'Feather Flags',
  'LGF': 'Large Format',
};

const EnhancedProductCard = ({ product, viewMode = 'grid' }) => {
  const features = extractFeatures(product.description);
  const categoryName = CATEGORY_NAMES[product.source_category] || product.source_category || product.category_code || 'Large Format';
  const price = product.retail_price || product.basePrice || 0;
  const isPricePerSqFt = product.pricing_type === 'per_sqft' || product.priceType === 'sqft';
  
  if (viewMode === 'list') {
    return (
      <Link
        to={`/LargePrint/product/${product.id}`}
        className="group block"
        data-testid={`product-row-${product.id}`}
      >
        <Card className="bg-ivory-100 border border-ivory-300/50 hover:shadow-luxury hover:border-gold-500/30 transition-all duration-300">
          <CardContent className="p-4 flex items-start gap-5">
            {/* Image */}
            <div className="w-28 h-28 bg-ivory-200 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={getImageUrl(product.image_url || product.image) || 'https://placehold.co/120x120/f5f3f0/9a8b7d?text=Product'}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                  e.target.src = 'https://placehold.co/120x120/f5f3f0/9a8b7d?text=Product';
                }}
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <Badge variant="outline" className="text-[10px] text-gold-600 border-gold-300 mb-2">
                {categoryName}
              </Badge>
              <h3 className="text-lg text-ivory-800 font-heading font-semibold group-hover:text-gold-700 transition-colors mb-2">
                {product.name}
              </h3>
              
              {/* Features */}
              {features.length > 0 && (
                <ul className="space-y-1 mb-2">
                  {features.slice(0, 2).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-ivory-500 font-body">
                      <CheckCircle2 className="w-3 h-3 text-gold-500 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-1">{feature}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* Price */}
            <div className="text-right flex-shrink-0">
              {price > 0 ? (
                <div>
                  <p className="text-2xl font-heading font-bold text-gold-600">
                    ${price.toFixed(2)}
                  </p>
                  {isPricePerSqFt && (
                    <p className="text-xs text-ivory-500 font-body">per sq ft</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-ivory-500 font-body">Contact for pricing</p>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Grid view (default)
  return (
    <Link
      to={`/LargePrint/product/${product.id}`}
      className="group block h-full"
      data-testid={`product-card-${product.id}`}
    >
      <Card className="h-full bg-ivory-100 border border-ivory-300/50 hover:shadow-luxury hover:border-gold-500/30 transition-all duration-300 overflow-hidden flex flex-col">
        {/* Image */}
        <div className="aspect-[4/3] bg-ivory-200 overflow-hidden relative">
          <img
            src={getImageUrl(product.image_url || product.image) || 'https://placehold.co/400x300/f5f3f0/9a8b7d?text=Product'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.src = 'https://placehold.co/400x300/f5f3f0/9a8b7d?text=Product';
            }}
          />
          {/* Category badge overlay */}
          <Badge 
            className="absolute top-3 left-3 bg-white/90 text-gold-700 border-0 text-[10px] font-body shadow-sm"
          >
            {categoryName}
          </Badge>
        </div>
        
        {/* Content */}
        <CardContent className="p-4 flex flex-col flex-1">
          {/* Product Name */}
          <h3 className="text-ivory-800 font-heading font-semibold line-clamp-2 mb-3 group-hover:text-gold-700 transition-colors text-base">
            {product.name}
          </h3>
          
          {/* Features List */}
          {features.length > 0 && (
            <ul className="space-y-1.5 mb-4 flex-1">
              {features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs text-ivory-600 font-body">
                  <CheckCircle2 className="w-3.5 h-3.5 text-gold-500 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-1">{feature}</span>
                </li>
              ))}
            </ul>
          )}
          
          {/* Price Section */}
          <div className="mt-auto pt-3 border-t border-ivory-200">
            {price > 0 ? (
              <div className="flex items-baseline justify-between">
                <div>
                  <span className="text-2xl font-heading font-bold text-gold-600">
                    ${price.toFixed(2)}
                  </span>
                  {isPricePerSqFt && (
                    <span className="text-xs text-ivory-500 font-body ml-1">/sq ft</span>
                  )}
                </div>
                <span className="text-xs text-gold-600 font-body font-medium group-hover:underline">
                  View Details →
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-sm text-ivory-500 font-body">Contact for pricing</span>
                <span className="text-xs text-gold-600 font-body font-medium group-hover:underline">
                  Learn More →
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default EnhancedProductCard;
